import { useEffect, useId, useMemo, useRef, useState } from "react";
import { LuCalendarClock } from "react-icons/lu";
import { createMeeting, getTeamMembers, queryAvailability } from "../../services/meeting_service";
import useLookupStore from "../../states/useLookupStore";
import AvailabilityStrip from "./AvailabilityStrip";
import { Input, Modal } from "@iq/models";
import "../modals/Modal.css";

const COMMON_TIMEZONES = [
	{ value: "UTC", label: "UTC" },
	{ value: "Eastern Standard Time", label: "EST" },
	{ value: "Central Standard Time", label: "CST" },
	{ value: "Mountain Standard Time", label: "MST" },
	{ value: "Pacific Standard Time", label: "PST" },
	{ value: "GMT Standard Time", label: "GMT" },
	{ value: "Central European Standard Time", label: "CET" },
	{ value: "India Standard Time", label: "IST" },
	{ value: "Australia/Sydney", label: "Sydney" },
	{ value: "Asia/Tokyo", label: "Tokyo" },
];

const DURATION_OPTIONS = [
	{ value: 15, label: "15 min" },
	{ value: 30, label: "30 min" },
	{ value: 60, label: "1 hour" },
	{ value: 90, label: "1.5 hours" },
	{ value: 120, label: "2 hours" },
	{ value: 180, label: "3 hours" },
];

function toLocalDateTimeString(date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const hour = String(date.getHours()).padStart(2, "0");
	const minute = String(date.getMinutes()).padStart(2, "0");
	const second = String(date.getSeconds()).padStart(2, "0");
	return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

/**
 * CreateMeetingModal - Modal for creating a new meeting/calendar event
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Callback to close the modal
 * @param {function} props.onSuccess - Callback on successful creation
 * @param {number} [props.dealId] - Associated deal ID
 * @param {number} [props.contactId] - Associated contact ID
 * @param {string} [props.contactEmail] - Contact email for attendee list
 */
function CreateMeetingModal({ isOpen, onClose, onSuccess, dealId, accountId, accountName = "", contactId, contactEmail, contactName }) {
	const formId = useId();
	const [selectedMemberLabel, setSelectedMemberLabel] = useState("");
	const [formData, setFormData] = useState({
		subject: "",
		description: "",
		startDate: new Date().toISOString().split("T")[0],
		startTime: "10:00",
		durationMinutes: 60,
		timeZone: "Eastern Standard Time",
		isTeamsOnline: true,
	});

	const [teamMembers, setTeamMembers] = useState([]);
	const [attendees, setAttendees] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const [membersLoading, setMembersLoading] = useState(true);
	const accounts = useLookupStore((state) => state.accounts);
	const accountsError = useLookupStore((state) => state.accountsError);
	const hydrateAccounts = useLookupStore((state) => state.hydrateAccounts);
	const [selectedAccountId, setSelectedAccountId] = useState(accountId || null);
	const [selectedAccountName, setSelectedAccountName] = useState(accountName);
	const [availabilityData, setAvailabilityData] = useState(null);
	const [availabilityLoading, setAvailabilityLoading] = useState(false);
	const [availabilityError, setAvailabilityError] = useState("");
	const availabilityRequestIdRef = useRef(0);
	const selectedDurationLabel = useMemo(() => {
		return DURATION_OPTIONS.find((option) => option.value === Number(formData.durationMinutes))?.label || "";
	}, [formData.durationMinutes]);
	const computedStartDateTime = useMemo(() => {
		return new Date(`${formData.startDate}T${formData.startTime}`);
	}, [formData.startDate, formData.startTime]);
	const computedEndDateTime = useMemo(() => {
		return new Date(computedStartDateTime.getTime() + Number(formData.durationMinutes) * 60000);
	}, [computedStartDateTime, formData.durationMinutes]);
	const selectedRange = {
		start: toLocalDateTimeString(computedStartDateTime),
		end: toLocalDateTimeString(computedEndDateTime),
	};

	const showAs = {
		busy: "#ef4444",
		tentative: "#f59e0b",
		oof: "#8b5cf6",
		workingElsewhere: "#6b7280",
	};

	useEffect(() => {
		if (isOpen) {
			hydrateAccounts();
		}
	}, [hydrateAccounts, isOpen]);

	useEffect(() => {
		if (isOpen) {
			setSelectedAccountId(accountId || null);
			setSelectedAccountName(accountName);
		}
	}, [accountId, accountName, isOpen]);

	useEffect(() => {
		const initialFormData = {
			subject: "",
			description: "",
			startDate: new Date().toISOString().split("T")[0],
			startTime: "10:00",
			durationMinutes: 60,
			timeZone: "Eastern Standard Time",
			isTeamsOnline: true,
		};

		const fetchMembers = async () => {
			try {
				const members = await getTeamMembers();
				setTeamMembers(members);

				// Pre-populate contact if email is provided
				if (contactEmail) {
					setAttendees([{
						email: contactEmail,
						name: contactName || contactEmail,
						isContact: true,
					}]);
				}
			} catch (err) {
				setError("Failed to load team members");
			} finally {
				setMembersLoading(false);
			}
		};

		if (isOpen) {
			setFormData({ ...initialFormData });
			setError("");
			setAvailabilityError("");
			setAvailabilityData(null);
			setSelectedMemberLabel("");
			// Reset attendees state each time modal opens
			setAttendees([]);
			fetchMembers();
		}
	}, [isOpen, contactEmail, contactName]);

	const teamEmails = useMemo(() => {
		return attendees.filter((attendee) => !attendee.isContact).map((attendee) => attendee.email);
	}, [attendees]);

	const canSubmit = useMemo(() => {
		return Boolean(formData.subject.trim()) && !loading;
	}, [formData.subject, loading]);

	const actionButtons = useMemo(() => ([
		{
			key: "create-meeting",
			text: loading ? "Creating..." : "Create Meeting",
			icon: <LuCalendarClock />,
			type: "submit",
			form: formId,
			disabled: !canSubmit,
			color: "accent",
		},
	]), [canSubmit, formId, loading]);

	const selectedTimezone = useMemo(() => {
		return COMMON_TIMEZONES.find((tz) => tz.value === formData.timeZone)?.label || "";
	}, [formData.timeZone]);

	useEffect(() => {
		if (!isOpen) {
			return;
		}

		if (teamEmails.length === 0) {
			availabilityRequestIdRef.current += 1;
			setAvailabilityData(null);
			setAvailabilityError("");
			setAvailabilityLoading(false);
			return;
		}

		const requestId = ++availabilityRequestIdRef.current;
		let cancelled = false;

		const refreshAvailability = async () => {
			setAvailabilityError("");
			setAvailabilityLoading(true);
			try {
				const result = await queryAvailability({
					teamEmails,
					startDate: formData.startDate,
					meetingDurationMins: Number(formData.durationMinutes),
					timeZone: formData.timeZone,
				});

				if (!cancelled && requestId === availabilityRequestIdRef.current) {
					setAvailabilityData(result);
				}
			} catch (err) {
				if (!cancelled && requestId === availabilityRequestIdRef.current) {
					setAvailabilityError(err.message || "Failed to check availability");
				}
			} finally {
				if (!cancelled && requestId === availabilityRequestIdRef.current) {
					setAvailabilityLoading(false);
				}
			}
		};

		refreshAvailability();

		return () => {
			cancelled = true;
		};
	}, [isOpen, teamEmails, formData.startDate, formData.startTime, formData.durationMinutes, formData.timeZone]);

	if (!isOpen) return null;

	const handleInputChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
		setError("");
		if (name === "startDate") {
			setAvailabilityData(null);
		}
	};

	const handleAddAttendee = (member) => {
		// Check if already added
		if (attendees.some(a => a.email === member.email)) {
			setError(`${member.name} already added`);
			return;
		}

		setAttendees([...attendees, {
			email: member.email,
			name: member.name,
			isContact: false,
		}]);
		setSelectedMemberLabel("");
		setError("");
	};

	const handleRemoveAttendee = (email) => {
		setAttendees((prev) => prev.filter((attendee) => attendee.email !== email));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError("");

		if (!formData.subject.trim()) {
			setError("Subject is required");
			return;
		}
		if (!selectedAccountId && !contactId && !dealId) {
			setError("Select an account for this meeting");
			return;
		}

		try {
			setLoading(true);

			// Combine date and time into ISO strings
			const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
			const endDateTime = new Date(startDateTime.getTime() + Number(formData.durationMinutes) * 60000);

			if (endDateTime <= startDateTime) {
				setError("End time must be after start time");
				return;
			}

			const attendeeEmails = attendees.map(a => a.email);

			const meetingData = {
				subject: formData.subject,
				description: formData.description,
				startTime: startDateTime,
				endTime: endDateTime,
				timeZone: formData.timeZone,
				location: "",
				isTeamsOnline: formData.isTeamsOnline,
				attendees: attendeeEmails,
				dealId,
				accountId: selectedAccountId,
				contactId,
				reminderMinutes: 15,
			};

			const result = await createMeeting(meetingData);
			onSuccess(result);
			onClose();
		} catch (err) {
			setError(err.message || "Failed to create meeting");
		} finally {
			setLoading(false);
		}
	};

	// Get list of available team members to add (exclude already added)
	const availableMembers = teamMembers.filter(
		member => !attendees.some(a => a.email === member.email)
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Create Meeting"
			icon={<LuCalendarClock />}
			actionButtons={actionButtons}
			errorMessage={error || accountsError}
			// shrink={true}
		>

				<form id={formId} onSubmit={handleSubmit} className="modal-form-container">
					<div className="form-fields-container">
					<div className="modal-form-field wide-form-field">
						<label htmlFor="meeting-account">Account *</label>
						<Input
							type="dropdown"
							id="meeting-account"
							placeholder="Select account"
							value={selectedAccountName}
							allowCustom={false}
							colorScheme="light"
							disabled={loading}
							options={accounts.map((account) => ({ value: account.id, label: account.name }))}
							onOptionSelect={(option) => {
								setSelectedAccountId(option.value);
								setSelectedAccountName(option.label);
							}}
						/>
					</div>

					<div className="modal-form-field wide-form-field">
						<label htmlFor="subject">Meeting Subject *</label>
						<Input
							id="subject"
							type="text"
							name="subject"
							value={formData.subject}
							onChange={handleInputChange}
							placeholder="e.g., Product Demo with Acme Corp"
							required
							disabled={loading}
							colorScheme="light"
						/>
					</div>

					<div className="modal-form-field modal-form-field-skinny">
							<label htmlFor="startDate">Start Date *</label>
							<Input
								id="startDate"
								type="date"
								name="startDate"
								value={formData.startDate}
								onChange={handleInputChange}
								required
								disabled={loading}
								colorScheme="light"
							/>
					</div>

					<div className="modal-form-field modal-form-field-skinny">
							<label htmlFor="startTime">Start Time *</label>
							<Input
								id="startTime"
								type="time"
								name="startTime"
								value={formData.startTime}
								onChange={handleInputChange}
								required
								disabled={loading}
								colorScheme="light"
							/>
					</div>

					<div className="modal-form-field modal-form-field-skinny">
							<label htmlFor="durationMinutes">Duration *</label>
							<Input
								type="dropdown"
								id="durationMinutes"
								name="durationMinutes"
								placeholder="Select duration"
								value={selectedDurationLabel}
								allowCustom={false}
								colorScheme="light"
								ariaLabel="Duration options"
								disabled={loading}
								options={DURATION_OPTIONS}
								onOptionSelect={(option) => setFormData((prev) => ({ ...prev, durationMinutes: option.value }))}
							/>
					</div>

					<div className="modal-form-field modal-form-field-skinny">
						<label htmlFor="timeZone">Timezone *</label>
						<Input
							type="dropdown"
							id="timeZone"
							name="timeZone"
							placeholder="Select timezone"
							value={selectedTimezone}
							allowCustom={false}
							colorScheme="light"
							ariaLabel="Timezone options"
							disabled={loading}
							options={COMMON_TIMEZONES}
							onOptionSelect={(option) => setFormData((prev) => ({ ...prev, timeZone: option.value }))}
						/>
					</div>

					<div className="modal-form-field wide-form-field">
						<label className="attendees-label">
							Attendees
							<span className="attendees-count">{attendees.length}</span>
						</label>
						<div className="attendees-control-row">
							{membersLoading ? (
								<div className="attendee-loading">Loading team members...</div>
							) : (
								<Input
									type="dropdown"
									id="meeting-team-members"
									name="meeting-team-members"
									className="attendees-dropdown"
									placeholder={availableMembers.length === 0 ? "All team members added" : "Select team member to add..."}
									value={selectedMemberLabel}
									allowCustom={false}
									colorScheme="light"
									ariaLabel="Team members"
									disabled={loading || availableMembers.length === 0}
									options={availableMembers.map((member) => ({ value: member.id, label: `${member.name} (${member.email})` }))}
									onOptionSelect={(option) => {
										const member = availableMembers.find((item) => item.id === option.value);
										if (member) {
											handleAddAttendee(member);
										}
									}}
								/>
							)}
						</div>
					</div>

					{attendees.some(a => !a.isContact) && (
						<div className="modal-form-field wide-form-field">
							<div className="team-availability-header">
								<label>Team Availability</label>
								{/* Legend */}
								<div className="avail-legend">
									{[["busy", "Busy"], ["tentative", "Tentative"], ["oof", "Out of Office"]].map(([key, label]) => (
										<span key={key} className="avail-legend-item">
											<span className="avail-legend-dot" style={{ background: showAs[key] }} />
											{label}
										</span>
									))}
								</div>
							</div>
							{availabilityError && (
								<div className="availability-inline-error">{availabilityError}</div>
							)}
							<AvailabilityStrip
								busySlots={availabilityData?.busySlots || []}
								queriedPeople={(availabilityData?.queriedEmails || []).map(email => ({
									email,
									name: teamMembers.find(m => m.email === email)?.name || email,
								}))}
								selectedRange={selectedRange}
								startDate={formData.startDate}
								onRemoveAttendee={handleRemoveAttendee}
								loading={availabilityLoading}
							/>
						</div>
					)}
					</div>
					<div className="modal-form-field wide-form-field">
						<label htmlFor="description">Description</label>
						<Input
							id="description"
							type="textarea"
							name="description"
							value={formData.description}
							onChange={handleInputChange}
							placeholder="Optional meeting description"
							rows={3}
							disabled={loading}
							colorScheme="light"
						/>
					</div>
					<div className="modal-form-field wide-form-field">
						<label className="meeting-toggle-row" htmlFor="isTeamsOnline">
							<input
								id="isTeamsOnline"
								type="checkbox"
								name="isTeamsOnline"
								checked={Boolean(formData.isTeamsOnline)}
								onChange={handleInputChange}
								disabled={loading}
							/>
							<span>Create as Microsoft Teams meeting (include join link in invite)</span>
						</label>
					</div>


				</form>
		</Modal>
	);
}

export default CreateMeetingModal;
